/**
 * Employee Web — `_auth` pathless authenticated layout.
 *
 * Phase 02 Plan 02-11 replaces the Phase 1 bare-Outlet wrapper with the
 * composed `<AppShell>` (shipped Plan 02-09) in `aud="mobile"` mode. Every
 * feature route landing under `_auth/*` automatically renders inside the
 * shell — topbar, user menu, billing banner, and the mobile drawer pattern.
 * `aud="mobile"` SUPPRESSES the DesktopOnlyScrim (SHELL-08 D-18 — mobile is
 * a first-class target for Employee Web).
 *
 * AUTH-13: `requireAuth` remains in `beforeLoad` — unauthenticated users are
 * redirected to `/login?redirect=<safe-path>` with open-redirect defense
 * (D-06 serialize-side; LoginPage deserializes the same way).
 *
 * NO raw network calls at the app layer — AppShell's internal
 * `usePrefsReconcile` drives the PATCH /me/preferences mutation via
 * @linkhr/api-client hooks (CONTEXT §D-28 step 4 + Plan 02-09 §"Branding
 * Fetch Integration"). No manual prefs-change wiring here.
 */
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  LayoutDashboard,
  Clock,
  Inbox,
  Banknote,
  MoreHorizontal,
  Settings,
  User,
  Bell,
} from "lucide-react";
import {
  Outlet,
  createFileRoute,
  useRouter,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { focusManager, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "@linkhr/i18n";
import {
  AppShell,
  filterNavByHref,
  RailTile,
  type BottomNavItem,
  type SearchableScreenSection,
} from "@linkhr/design-system";
import {
  JurisdictionProvider,
  TenantSlugContext,
  asTenantSlug,
  isApiError,
  qk,
  useDeletePushSubscription,
  useEntitlements,
  useMeEmployee,
  revalidateMePermissions,
} from "@linkhr/api-client";
import { AssistantProvider, AssistantGlobalSurface, AssistantSlotOutlet } from "@linkhr/assistant";
import { performLogout, requireAuth, resolveTenant } from "@linkhr/auth";
import { useAuth } from "@linkhr/auth/react";
import { AuthSplash } from "@linkhr/auth/pages";
import {
  NotYetAvailable,
  isJoinerPath,
  useJoining,
  type Joining,
} from "@linkhr/employee-self-service";
import { useJurisdictionFence } from "../features/jurisdiction/fence";
import { employeeNav, joinerNav } from "../nav";
import { employeeSearchableScreens } from "../registries/searchableScreens";
import { useNavBadges } from "../features/nav/useNavBadges";
import { useGlobalSearchSections } from "../features/search/useGlobalSearchSections";
import { env } from "../env";
import { useEmployeeWebAssistantConfig } from "../features/assistant/assistantConfig";
import { NotificationBellContainer } from "../features/notifications/NotificationBellContainer";
import { AppSwitcherButton } from "../features/app-switcher/AppSwitcherButton";

export const Route = createFileRoute("/_auth")({
  beforeLoad: requireAuth,
  component: EmployeeWebAuthLayout,
});

function EmployeeWebAuthLayout(): JSX.Element {
  const tenantSlug = asTenantSlug(resolveTenant({ fallbackSlug: env.VITE_DEV_TENANT_SLUG }));
  // Start the employee read now, beside the jurisdiction read: the shell waits
  // for both (is this person before their first day?), and reading the record
  // only once the jurisdiction resolves would put a second round trip in front
  // of every member's first paint.
  useMeEmployee();
  return (
    <TenantSlugContext.Provider value={tenantSlug}>
      {/* The tenant's country profile is resolved once, above the shell, because
          the sidebar / bottom bar / palette all have to know which screens
          exist before they render a single entry. */}
      <JurisdictionProvider tenantSlug={tenantSlug} loadingFallback={<AuthSplash />}>
        <JoiningAwareShell tenantSlug={tenantSlug} />
      </JurisdictionProvider>
    </TenantSlugContext.Provider>
  );
}

/**
 * Decides, before any nav paints, whether the caller is still before their
 * first day: a joiner gets a reduced shell, so the full one never mounts for a
 * frame. Splash while `/employees/me` is unknown.
 */
function JoiningAwareShell({
  tenantSlug,
}: {
  tenantSlug: ReturnType<typeof asTenantSlug>;
}): JSX.Element {
  const joining = useJoining();
  const queryClient = useQueryClient();

  // Any call refused with EMPLOYEE_NOT_STARTED means the record's start date
  // and ours disagree (it moved, or we hold a stale answer): read it again.
  useEffect(() => {
    const meKey = qk.me.employee();
    const recheck = (err: unknown): void => {
      if (!isApiError(err) || err.errorCode !== "EMPLOYEE_NOT_STARTED") return;
      if (queryClient.isFetching({ queryKey: meKey }) > 0) return;
      void queryClient.invalidateQueries({ queryKey: meKey });
    };
    const offQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") recheck(event.action.error);
    });
    const offMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") recheck(event.action.error);
    });
    return () => {
      offQueries();
      offMutations();
    };
  }, [queryClient]);

  // While joining, the first day may arrive (or move) while the tab sits open:
  // look at the record again whenever the window comes back into focus.
  useEffect(() => {
    if (joining.state !== "joining") return undefined;
    return focusManager.subscribe((focused) => {
      if (focused) void queryClient.invalidateQueries({ queryKey: qk.me.employee() });
    });
  }, [joining.state, queryClient]);

  if (joining.state === "unknown") return <AuthSplash />;
  return <EmployeeWebShell tenantSlug={tenantSlug} joining={joining} />;
}

/**
 * The shell interior — rendered UNDER <JurisdictionProvider> so the country
 * fence resolves before any nav list is built.
 */
function EmployeeWebShell({
  tenantSlug,
  joining,
}: {
  tenantSlug: ReturnType<typeof asTenantSlug>;
  joining: Joining;
}): JSX.Element {
  // Before the first day only a handful of screens exist for this person.
  const isJoining = joining.state === "joining";
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const router = useRouter();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // The tenant's resolved entitlements — the same channel hr-admin uses. This
  // app had no `/me/permissions` read at all before now; its auth bootstrap
  // (`effectivePermissions`) is a deliberately fail-closed, no-api-client
  // boundary and is NOT extended.
  const entitlements = useEntitlements(tenantSlug);

  // Entitlements can move in another session (a plan change, a platform
  // override). Re-read on navigation — the helper is throttled to at most one
  // refetch per tenant per 15s, and the endpoint's ETag makes an unchanged
  // answer a 304-sized round-trip.
  useEffect(() => {
    revalidateMePermissions(queryClient, tenantSlug);
  }, [pathname, queryClient, tenantSlug]);

  // Assistant availability, as a runtime entitlement rather than a build flag.
  // Off for a joiner: it reads screens that open on the first day.
  const baseAssistantConfig = useEmployeeWebAssistantConfig();
  const assistantConfig = useMemo(
    () => (isJoining ? { ...baseAssistantConfig, enabled: false } : baseAssistantConfig),
    [isJoining, baseAssistantConfig],
  );
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);
  // Revoked mid-session: the provider has already torn its own surface down by
  // the time this fires. All that is left is the ROUTE — an employee sitting on
  // the assistant page would otherwise stare at a dead screen. Only navigate if
  // that is where they actually are, and `replace` so a page nobody may open is
  // not left in the history for Back to land on.
  const handleAssistantRevoked = useCallback(() => {
    if (pathnameRef.current.startsWith("/me/assistant")) {
      void navigate({ to: "/me", replace: true });
    }
  }, [navigate]);

  // SPA-nav intercept — palette + (future) sidebar nav both route through
  // this; without it, full browser navigation re-bootstraps the app and
  // triggers a /auth/refresh storm on every click (mirrors hr-admin Plan
  // 02-10's onSidebarNavigate rationale).
  const onShellNavigate = (href: string): void => {
    void navigate({ to: href });
  };

  const locale: "en" | "ar" = i18n.language.startsWith("ar") ? "ar" : "en";

  const onLocaleChange = (next: "en" | "ar"): void => {
    void i18n.changeLanguage(next);
  };

  const displayName = auth.user?.displayName ?? "";
  // Someone who signs in with a username has no email: show the username.
  const email =
    auth.user !== null && auth.user.email !== "" ? auth.user.email : (auth.user?.username ?? null);
  const avatarUrl = null;
  // Phase 1's AuthSnapshot carries `tenantId` (the UUID), not a human-
  // friendly tenant name. Pass null — AppShell's useBrandingFetch falls
  // back to the neutral "Link HR" display name when the branding fetch
  // settles (or stays neutral if it fails).
  const tenantName = null;

  // <Toaster> is mounted at __root.tsx so unauth routes (/auth/*) inherit
  // useToast() context too. Authenticated features (save toasts, photo
  // upload, NotificationBellContainer) inherit it transparently.
  const deletePushSubscription = useDeletePushSubscription(tenantSlug);

  const onLogout = async (): Promise<void> => {
    // Tear down web push BEFORE performLogout clears the access token —
    // otherwise HR notifications keep arriving on this device after sign-out.
    // Capped at 1.5s and fully best-effort: `navigator.serviceWorker.ready`
    // never resolves without an active worker and the DELETE can be slow, so
    // a timeout race guarantees logout is never blocked on push teardown.
    const teardownPush = async (): Promise<void> => {
      if (!("serviceWorker" in navigator)) return;
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;
      await deletePushSubscription
        .mutateAsync({ endpoint: subscription.endpoint })
        .catch(() => undefined);
      await subscription.unsubscribe().catch(() => false);
    };
    await Promise.race([
      teardownPush().catch(() => undefined),
      new Promise<void>((resolve) => setTimeout(resolve, 1500)),
    ]);

    await performLogout({
      queryClient,
      apiBaseUrl: env.VITE_API_BASE_URL,
    });
    // performLogout ends with window.location.assign(); router.invalidate()
    // is a defensive cache flush for test environments where hard-nav is
    // skipped (Plan 02-10 mirrors this pattern).
    await router.invalidate();
  };

  // Country fence — `allowHref` drops every screen the tenant's jurisdiction
  // does not have (or that we could not resolve) from each list below that
  // addresses a screen by href.
  const fence = useJurisdictionFence();
  // Memoized (map fact: this app filtered on every render while hr-admin did
  // not) — the result now feeds AppShell's own entitlement filtering too, so an
  // unstable array would re-filter the whole tree on every render.
  const nav = useMemo(
    () => (isJoining ? joinerNav : filterNavByHref(employeeNav, fence.allowHref)),
    [isJoining, fence.allowHref],
  );

  /* Phase 28 (CC-149) — global search wiring. Returns a composed sections
   * array (live results from /api/v1/search prepended to the static
   * screen registry) + the typed-value forwarder. */
  const liveSearch = useGlobalSearchSections({
    tenantSlug,
    baseSections: employeeSearchableScreens,
  });

  // Applied to the COMPOSED sections, so a fenced screen can't reach the
  // palette. Only screen sections address a route up front; verb and
  // lazily-loaded sections carry no href to fence at this point.
  const searchSections: SearchableScreenSection[] = [];
  for (const section of liveSearch.sections) {
    if (section.kind === "action" || section.kind === "asyncSection") {
      searchSections.push(section);
      continue;
    }
    const items = section.items.filter((item) => fence.allowHref(item.route));
    if (items.length > 0) searchSections.push({ ...section, items });
  }

  // Sidebar pending-work counts (unread Messages; team Approvals queue for
  // managers). Gated internally on the same permissions as the nav entries.
  const navBadges = useNavBadges(
    tenantSlug,
    auth.effectivePermissions ?? new Set<string>(),
    isJoining,
  );

  // WS-4 — mobile bottom bar (≤ md). Five thumb-reachable destinations from the
  // existing nav + a "More" item (no href → opens the mobile drawer for the
  // rest). Labels come from the same `common:nav.*` bundle the Sidebar uses.
  const { t } = useTranslation("common");
  const bottomNav: readonly BottomNavItem[] = useMemo(
    () =>
      isJoining
        ? [
            { key: "home", label: t("nav.home"), icon: LayoutDashboard, href: "/me" },
            { key: "profile", label: t("nav.profile"), icon: User, href: "/me/profile/personal" },
            {
              key: "notifications",
              label: t("nav.notifications"),
              icon: Bell,
              href: "/me/notifications",
            },
          ]
        : [
            { key: "home", label: t("nav.dashboard"), icon: LayoutDashboard, href: "/me" },
            { key: "punch", label: t("nav.attendance"), icon: Clock, href: "/me/my-attendance" },
            { key: "requests", label: t("nav.my_requests"), icon: Inbox, href: "/me/requests" },
            { key: "payslip", label: t("nav.payslip"), icon: Banknote, href: "/me/payroll" },
            { key: "more", label: t("nav.more", { defaultValue: "More" }), icon: MoreHorizontal },
          ],
    [t, isJoining],
  );

  // Rail-bottom slot: the Preferences gear (the entry that used to sit in the
  // Company group; ungated, exactly as the entry was). Lit + aria-current on
  // /me/preferences/*.
  const preferencesLabel = t("nav.preferences", { defaultValue: "Preferences" });
  const preferencesActive = pathname.startsWith("/me/preferences");
  const railBottom = (
    <RailTile
      icon={Settings}
      label={preferencesLabel}
      selected={preferencesActive}
      {...(preferencesActive ? { "aria-current": "page" as const } : {})}
      onClick={() => onShellNavigate("/me/preferences")}
    />
  );

  // A joiner sees every screen outside their few as "opens on your first day".
  const page =
    isJoining && !isJoinerPath(pathname) ? (
      <NotYetAvailable startsOn={joining.startsOn} />
    ) : (
      <Outlet />
    );

  return (
    <AppShell
      nav={nav}
      aud="mobile"
      appName="Employee Web — Link HR"
      user={{
        displayName,
        email,
        roles: auth.roles,
        // Plan 08.1-02 — perm-gated nav entries (e.g., future /team/*
        // group with `*:read:team` perms) read this Set.
        permissions: auth.effectivePermissions ?? new Set<string>(),
        // Tenant entitlements — the features channel on the SAME prop the
        // permissions ride. Empty until the bootstrap read lands, which is the
        // fail-closed direction for any entry declaring a featureKey.
        features: entitlements.features,
        avatarUrl,
      }}
      tenantName={tenantName}
      tenantSlug={tenantSlug}
      // Full browser URL. The router basepath is "/me" AND the route tree bakes
      // "/me" into every path (files are _auth.me.*), so real browser URLs are
      // "/me/me/*". The user-menu Profile is a raw <a href>, so it needs the
      // actual browser URL — "/me/personal" strips to "/personal" and 404s.
      profileHref="/me/me/profile/personal"
      // Router basepath ("/me") — prefixes the sidebar's rendered anchor
      // hrefs so a hard load / new-tab / copy-link resolves under the app
      // base (real URLs are "/me/me/*"). onShellNavigate still gets the raw
      // href, so SPA left-click nav is unchanged.
      navBasePath={router.basepath}
      onLogout={onLogout}
      locale={locale}
      onLocaleChange={onLocaleChange}
      notificationsSlot={<NotificationBellContainer />}
      assistantSlot={<AssistantSlotOutlet />}
      // Topbar end-slot: surfaces a "Switch to HR Admin" button for users who
      // also have HR Admin access (renders null otherwise).
      personaSlot={<AppSwitcherButton />}
      onNavigate={onShellNavigate}
      // No palette for a joiner: search opens on the first day.
      {...(isJoining
        ? {}
        : {
            searchableScreens: searchSections,
            onSearchValueChange: liveSearch.onSearchValueChange,
            searchTruncated: liveSearch.truncated,
          })}
      activeHref={pathname}
      navBadges={navBadges}
      railBottom={railBottom}
      bottomNav={bottomNav.filter((item) => item.href === undefined || fence.allowHref(item.href))}
    >
      {/* The env kill switch is a BUILD-TIME constant, so branching on it here
          is sound — it cannot flip and remount the Outlet. The entitlement is
          NOT: it rides `config.enabled` into an always-mounted provider.
          Branching on the entitlement here would unmount every screen. */}
      {env.VITE_ASSISTANT_ENABLED ? (
        <AssistantProvider config={assistantConfig} onDisabledWhileActive={handleAssistantRevoked}>
          {page}
          <AssistantGlobalSurface />
        </AssistantProvider>
      ) : (
        page
      )}
    </AppShell>
  );
}
